import React from 'react';
import { TextBox } from '../../../components/ui/TextBox';
import { Button } from '../../../components/ui/Button';
import type { PaymentEnvironment, PaymentProvider } from './presenter';

export type ViewProps = {
  loading: boolean;
  saving: boolean;
  error: string | null;
  provider: PaymentProvider;
  setProvider: (v: PaymentProvider) => void;
  environment: PaymentEnvironment;
  setEnvironment: React.Dispatch<React.SetStateAction<PaymentEnvironment>>;
  merchantId: string;
  setMerchantId: (v: string) => void;
  baseUrlApi: string;
  setBaseUrlApi: (v: string) => void;
  baseUrlApp: string;
  setBaseUrlApp: (v: string) => void;
  clientKeyMasked?: string | null;
  serverKeyMasked?: string | null;
  privateKeyMasked?: string | null;
  newClientKey: string;
  setNewClientKey: (v: string) => void;
  newServerKey: string;
  setNewServerKey: (v: string) => void;
  newPrivateKey: string;
  setNewPrivateKey: (v: string) => void;
  lowStockThreshold: number | null | undefined;
  setLowStockThreshold: (v: number | null) => void;
  onSave: () => void;
  onReload: () => void;
};

export const SettingsPaymentView: React.FC<ViewProps> = ({
  loading,
  saving,
  error,
  provider,
  setProvider,
  environment,
  setEnvironment,
  merchantId,
  setMerchantId,
  baseUrlApi,
  setBaseUrlApi,
  baseUrlApp,
  setBaseUrlApp,
  clientKeyMasked,
  serverKeyMasked,
  privateKeyMasked,
  newClientKey,
  setNewClientKey,
  newServerKey,
  setNewServerKey,
  newPrivateKey,
  setNewPrivateKey,
  lowStockThreshold,
  setLowStockThreshold,
  onSave,
  onReload,
}) => {
  const isDoku = provider === 'doku';

  return (
    <div className="w-full">
      <h1 className="mb-4 text-xl font-semibold">Payment Settings</h1>
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium">Payment Provider</label>
          <select
            className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={provider}
            onChange={e => setProvider(e.target.value as any)}
            disabled={loading || saving}
          >
            <option value="none">None (Disabled)</option>
            <option value="midtrans">Midtrans</option>
            <option value="doku">Doku</option>
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">Environment</label>
          <select
            className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={environment}
            onChange={e => setEnvironment(e.target.value as any)}
            disabled={loading || saving}
          >
            <option value="sandbox">Sandbox</option>
            <option value="production">Production</option>
          </select>
        </div>

        <TextBox
          label={isDoku ? 'Mall ID / Merchant ID' : 'Merchant ID'}
          placeholder={isDoku ? 'Enter Mall ID' : 'Enter Merchant ID'}
          value={merchantId}
          onChange={e => setMerchantId(e.target.value)}
          disabled={loading || saving}
        />

        <TextBox
          label="API Base URL (optional)"
          placeholder={'Leave blank to use environment default'}
          value={baseUrlApi}
          onChange={e => setBaseUrlApi(e.target.value)}
          disabled={loading || saving}
        />
        {!isDoku && (
          <TextBox
            label="App Base URL (optional)"
            placeholder={'Leave blank to use environment default'}
            value={baseUrlApp}
            onChange={e => setBaseUrlApp(e.target.value)}
            disabled={loading || saving}
          />
        )}

        <TextBox
          label="Low Stock Threshold"
          placeholder={'e.g., 2'}
          value={lowStockThreshold == null ? '' : String(lowStockThreshold)}
          onChange={e => {
            const v = e.target.value.trim();
            if (v === '') return setLowStockThreshold(null);
            const n = Number(v);
            if (Number.isNaN(n)) return; // ignore invalid
            setLowStockThreshold(Math.max(0, Math.floor(n)));
          }}
          disabled={loading || saving}
        />

        <TextBox
          label={isDoku ? 'Client ID (optional)' : 'Client Key (optional)'}
          placeholder={clientKeyMasked ? `Current: ${clientKeyMasked} (leave blank to keep)` : isDoku ? 'Enter Client ID' : 'Enter Client Key'}
          value={newClientKey}
          onChange={e => setNewClientKey(e.target.value)}
          disabled={loading || saving}
        />
        <TextBox
          label={isDoku ? 'Shared Key (optional)' : 'Server Key (optional)'}
          placeholder={serverKeyMasked ? `Current: ${serverKeyMasked} (leave blank to keep)` : isDoku ? 'Enter Shared Key' : 'Enter Server Key'}
          value={newServerKey}
          onChange={e => setNewServerKey(e.target.value)}
          disabled={loading || saving}
        />

        {isDoku && (
          <TextBox
            label="Private Key (optional)"
            placeholder={privateKeyMasked ? `Current: ${privateKeyMasked} (leave blank to keep)` : 'Enter Private Key'}
            value={newPrivateKey}
            onChange={e => setNewPrivateKey(e.target.value)}
            disabled={loading || saving}
          />
        )}

        {error && <div className="text-sm text-destructive">{error}</div>}

        <div className="flex gap-2">
          <Button onClick={onSave} disabled={saving || loading}>{saving ? 'Saving...' : 'Save'}</Button>
          <Button variant="outline" onClick={onReload} disabled={saving}>Reload</Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPaymentView;
